import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import {
	TextField,
	Button,
	Box,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	FormControl,
	FormHelperText,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ToastContainer } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs'; // Correct import for dayjs
import Swal from 'sweetalert2';
import validate from '../../Validation'; // Import the validation component
import HomeSmallHeader from '../../../../components/component/resused-component/HomeSmallHeader';
import ContanierHeading from '../../../../components/component/resused-component/ContanierHeading';
import { getAllEvents } from '../../../../services/api/MenuAPI';
import { bookusApi } from '../../../../services/api/UserAPI';

// import { EventTypes } from '../../../../types/index';

interface FormData {
	name: string;
	email: string;
	contactNo: string;
	eventType: string;
	noOfPeople: string;
	address: string;
	area: string;
	description: string;
	date: Dayjs | null; // Use Dayjs type
}

interface EventTypes {
	_id: string;
	eventType: string;
	menuType: string[];
}

interface Errors {
	name?: string;
	email?: string;
	contactNo?: string;
	eventType?: string;
	noOfPeople?: string;
	address?: string;
	area?: string;
	description?: string;
	date?: string;
}

const BookNowForm = () => {
	const [formData, setFormData] = useState<FormData>({
		name: '',
		email: '',
		contactNo: '',
		eventType: '',
		noOfPeople: '',
		address: '',
		area: '',
		description: '',
		date: null,
	});
	const [eventTypesData, setEventTypesData] = useState<EventTypes[]>([]);
	const [loadingData, selLoadingData] = useState(false);
	const [errors, setErrors] = useState<Errors>({});

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await getAllEvents();
				if (response.success) {
					setEventTypesData(response.data.eventTypes);
				}
			} catch (error) {
				console.log(error);
			}
		};
		fetchData();
	}, []);

	const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});

		// Validate the specific field being changed
		const fieldErrors = validate({ ...formData, [name]: value });
		if (!fieldErrors[name as keyof FormData]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: undefined,
			}));
		}
	};

	const handleSelectChange = (e: SelectChangeEvent<string>) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});

		// Validate the specific field being changed
		const fieldErrors = validate({ ...formData, [name]: value });
		if (!fieldErrors[name as keyof FormData]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: undefined,
			}));
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		const validationErrors = validate(formData);
		if (Object.keys(validationErrors).length === 0) {
			const fetchData = async () => {
				const data = {
					name: formData.name,
					email: formData.email,
					mobile: formData.contactNo,
					eventType: formData.eventType,
					eventDate: formData.date,
					noOfPeoples: formData.noOfPeople,
					address: formData.address,
					country: formData.area,
					description: formData.description,
				};
				try {
					selLoadingData(true);
					const response = await bookusApi(data);
					if (response.success) {
						setFormData({
							name: '',
							email: '',
							contactNo: '',
							eventType: '',
							noOfPeople: '',
							address: '',
							area: '',
							description: '',
							date: null,
						});
						// toast.success(response?.data?.message);
						Swal.fire({
							icon: 'success',
							title: 'Thank you!',
							text: `${response?.data?.message}`,
							showConfirmButton: true,
							customClass: {
								popup: 'poppins-font',
							},
							backdrop: `rgba(0,0,0,0.4) left top no-repeat`,
							background: '#fff',
						});
					}
				} catch (error) {
					console.error('Error submitting form:', error);
				} finally {
					selLoadingData(false);
				}
			};
			fetchData();
		} else {
			setErrors(validationErrors);
		}
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box
				component="form"
				onSubmit={handleSubmit}
				noValidate
				sx={{
					flexGrow: 1,
					p: 2,
					pb: 5,
					backgroundImage: 'url(/assets/public/assets/booknow-banner.png)',
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					position: 'relative',
				}}
			>
				<HomeSmallHeader text="Book us" />
				{/* <Typography variant="h4" align="center">
					Where you want Our Services
				</Typography> */}
				<ContanierHeading name="Where you want Our Services" />
				<Grid
					container
					spacing={2}
					justifyContent="center"
					alignItems="center"
					marginTop={'27px'}
				>
					<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8 }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								<TextField
									color="secondary"
									fullWidth
									label="Name"
									name="name"
									value={formData.name}
									onChange={handleTextFieldChange}
									error={!!errors.name}
									helperText={errors.name}
									size="small"
									slotProps={{
										input: {
											sx: {
												fontSize: '14px', // <-- input text size
											},
										},
									}}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								<TextField
									color="secondary"
									fullWidth
									label="Email"
									name="email"
									value={formData.email}
									onChange={handleTextFieldChange}
									error={!!errors.email}
									helperText={errors.email}
									size="small"
									slotProps={{
										input: {
											sx: {
												fontSize: '14px', // <-- input text size
											},
										},
									}}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								{/* <TextField
									color="secondary"
									fullWidth
									label="Mobile"
									name="contactNo"
									value={formData.contactNo}
									onChange={handleTextFieldChange}
									error={!!errors.contactNo}
									helperText={errors.contactNo}
									size="small"
									slotProps={{
										input: {
											sx: {
												fontSize: '14px', // <-- input text size
											},
										},
									}}
								/> */}
								<TextField
									color="secondary"
									fullWidth
									label="Mobile"
									name="contactNo"
									value={formData.contactNo}
									error={!!errors.contactNo}
									helperText={errors.contactNo}
									size="small"
									onChange={(e) => {
										const value = e.target.value;
										if (/^\d*$/.test(value)) {
											const sanitizedValue = value.replace(/^0+/, ''); // Remove leading zeros

											setFormData((prev) => ({
												...prev,
												contactNo: sanitizedValue,
											}));

											const fieldErrors = validate({
												...formData,
												contactNo: sanitizedValue,
											});

											if (!fieldErrors.contactNo) {
												setErrors((prevErrors) => ({
													...prevErrors,
													contactNo: undefined,
												}));
											}
										}
									}}
									slotProps={{
										// input: {
										// 	sx: {
										// 		fontSize: '14px',
										// 	},
										// },
										htmlInput: {
											inputMode: 'numeric',
											pattern: '[0-9]*',
											maxLength: 10, // optional: limit to 10 digits
										},
									}}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								<FormControl
									fullWidth
									error={!!errors.eventType}
									size="small"
									color="secondary"
								>
									<InputLabel id="demo-simple-select-label">
										Event Type
									</InputLabel>
									<Select
										color="secondary"
										name="eventType"
										labelId="demo-simple-select-label"
										id="demo-simple-select"
										value={formData.eventType}
										onChange={handleSelectChange}
										label="Event Type"
										slotProps={{
											input: {
												sx: {
													fontSize: '14px', // <-- input text size
												},
											},
										}}
									>
										<MenuItem value="" disabled>
											Select Event Type
										</MenuItem>
										{eventTypesData.map((value) => (
											<MenuItem key={value.eventType} value={value._id}>
												{value.eventType}
											</MenuItem>
										))}
									</Select>
									{errors.eventType && (
										<FormHelperText>{errors.eventType}</FormHelperText>
									)}
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								{/* <DatePicker
									label="Date"
									value={formData.date}
									minDate={dayjs().add(1, 'day')}
									onChange={(newValue: Dayjs | null) => {
										setFormData((prevFormData) => ({
											...prevFormData,
											date: newValue,
										}));
										// Validate the date field
										const fieldErrors = validate({
											...formData,
											date: newValue,
										});
										if (!fieldErrors.date) {
											setErrors((prevErrors) => ({
												...prevErrors,
												date: undefined,
											}));
										}
									}}
									slotProps={{
										textField: {
											fullWidth: true,
											error: !!errors.date,
											helperText: errors.date,
											size: 'small',
										},
									}}
								/> */}
								<DatePicker
									label="Date"
									value={formData.date}
									minDate={dayjs().add(1, 'day')}
									onChange={(newValue: Dayjs | null) => {
										setFormData((prevFormData) => ({
											...prevFormData,
											date: newValue,
										}));
										const fieldErrors = validate({
											...formData,
											date: newValue,
										});
										if (!fieldErrors.date) {
											setErrors((prevErrors) => ({
												...prevErrors,
												date: undefined,
											}));
										}
									}}
									slotProps={{
										textField: {
											fullWidth: true,
											error: !!errors.date,
											helperText: errors.date,
											size: 'small',
											// slotProps: {
											// 	input: {
											// 		sx: {
											// 			fontSize: '12px', // input text font
											// 		},
											// 	},
											// 	inputLabel: {
											// 		sx: {
											// 			fontSize: '12px', // label font
											// 		},
											// 	},
											// },
											sx: {
												'& .MuiInputBase-input': {
													fontSize: '14px', // input field text
												},
											},
										},
										popper: {
											placement: 'auto-start',
										},
									}}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								<TextField
									color="secondary"
									fullWidth
									label="No of People"
									name="noOfPeople"
									value={formData.noOfPeople}
									onChange={(e) => {
										const value = e.target.value;
										if (/^\d*$/.test(value)) {
											const sanitizedValue = value.replace(/^0+/, '');
											setFormData({
												...formData,
												noOfPeople: sanitizedValue,
											});
											// Validate the specific field being changed
											const fieldErrors = validate({
												...formData,
												noOfPeople: sanitizedValue,
											});
											if (!fieldErrors.noOfPeople) {
												setErrors((prevErrors) => ({
													...prevErrors,
													noOfPeople: undefined,
												}));
											}
										}
									}}
									error={!!errors.noOfPeople}
									helperText={errors.noOfPeople}
									size="small"
									variant="outlined"
									// slotProps={{
									// 	input: {
									// 		sx: {
									// 			fontSize: '14px', // <-- input text size
									// 		},
									// 	},
									// }}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								{/* <TextField
									color="secondary"
									fullWidth
									label="Mandapam Name"
									name="address"
									value={formData.address}
									onChange={handleTextFieldChange}
									error={!!errors.address}
									helperText={errors.address}
									size="small"
									slotProps={{
										input: {
											sx: {
												fontSize: '14px', // <-- input text size
											},
										},
									}}
								/> */}

								<TextField
									color="secondary"
									fullWidth
									label="Mandapam Name"
									name="address"
									value={formData.address}
									onChange={handleTextFieldChange}
									error={!!errors.address}
									helperText={errors.address}
									size="small"
									variant="outlined"
									// slotProps={{
									// 	input: {
									// 		sx: {
									// 			fontSize: '14px',
									// 		},
									// 	},
									// 	root: {
									// 		sx: {
									// 			'& .MuiOutlinedInput-notchedOutline legend': {
									// 				padding: 0, // 👈 no padding normally
									// 			},
									// 			'& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend':
									// 				{
									// 					padding: '0 6px', // ✅ works when value is filled and label is shrunk
									// 				},
									// 			'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline legend':
									// 				{
									// 					padding: '0 6px', // ✅ works when focused
									// 				},
									// 		},
									// 	},
									// }}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								<TextField
									color="secondary"
									fullWidth
									label="Area"
									name="area"
									value={formData.area}
									onChange={handleTextFieldChange}
									error={!!errors.area}
									helperText={errors.area}
									size="small"
									variant="outlined"
									// slotProps={{
									// 	input: {
									// 		sx: {
									// 			fontSize: '14px', // <-- input text size
									// 		},
									// 	},
									// }}
								/>
								{/* <TextField
									color="secondary"
									fullWidth
									label="Pincode"
									name="area"
									value={formData.area}
									onChange={(e) => {
										const value = e.target.value;
										if (/^\d*$/.test(value)) {
											// Only allow digits
											let sanitizedValue = value.replace(/^0+/, ''); // Remove leading zeros
											if (sanitizedValue.length > 6) {
												sanitizedValue = sanitizedValue.slice(0, 6); // Limit to 6 digits
											}

											setFormData((prev) => ({
												...prev,
												area: sanitizedValue,
											}));

											const fieldErrors = validate({
												...formData,
												area: sanitizedValue,
											});

											if (!fieldErrors.area) {
												setErrors((prevErrors) => ({
													...prevErrors,
													area: undefined,
												}));
											}
										}
									}}
									error={!!errors.area}
									helperText={errors.area}
									size="small"
									slotProps={{
										input: {
											sx: {
												fontSize: '14px',
											},
										},
										htmlInput: {
											maxLength: 6,
											inputMode: 'numeric',
											pattern: '[0-9]*',
										},
									}}
								/> */}
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
								<TextField
									color="secondary"
									fullWidth
									label="Description"
									name="description"
									value={formData.description}
									onChange={handleTextFieldChange}
									error={!!errors.description}
									helperText={errors.description}
									multiline
									// rows={4}
									size="small"
									variant="outlined"
								/>
							</Grid>
							<Grid
								size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
								sx={{ textAlign: 'center' }}
							>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									loading={loadingData}
									loadingIndicator="Sending..."
								>
									Send Request
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>
			<ToastContainer />
		</LocalizationProvider>
	);
};

export default BookNowForm;
